import { forwardRef, useMemo, type SelectHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { usePartTypes } from '@/features/part-types/queries';
import { PART_TYPE_CATEGORIES } from '@/features/part-types/types';
import { cn } from '@/lib/utils';

interface PartTypePickerProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  value: string | null;
  onChange: (typeId: string | null) => void;
  /** Hide inactive types from the list (default: true). */
  hideInactive?: boolean;
  /** Allow the empty option (default: false). */
  allowEmpty?: boolean;
  emptyLabel?: string;
}

/**
 * PartTypePicker — native <select> wired to usePartTypes() and
 * grouped by category via <optgroup>. Each option label combines
 * the type name and the code in monospace-friendly format.
 *
 * Used by the Part create form (F7.2) and any future module that
 * needs to pick a part-type catalog entry.
 */
export const PartTypePicker = forwardRef<
  HTMLSelectElement,
  PartTypePickerProps
>(
  (
    {
      value,
      onChange,
      hideInactive = true,
      allowEmpty = false,
      emptyLabel,
      className,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { data, isLoading } = usePartTypes();

    const grouped = useMemo(() => {
      const filtered = data
        ? hideInactive
          ? data.filter((d) => d.isActive)
          : data
        : [];
      return PART_TYPE_CATEGORIES.map((category) => ({
        category,
        items: filtered.filter((d) => d.category === category),
      }));
    }, [data, hideInactive]);

    return (
      <select
        ref={ref}
        value={value ?? ''}
        onChange={(e) =>
          onChange(e.target.value === '' ? null : e.target.value)
        }
        disabled={isLoading || rest.disabled}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      >
        {allowEmpty && <option value="">{emptyLabel ?? '—'}</option>}
        {grouped.map(({ category, items }) =>
          items.length === 0 ? null : (
            <optgroup
              key={category}
              label={t(`partType.category.${category}`)}
            >
              {items.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.name} ({pt.code})
                </option>
              ))}
            </optgroup>
          ),
        )}
      </select>
    );
  },
);
PartTypePicker.displayName = 'PartTypePicker';
